import { memo, useEffect, useRef, useState, type ComponentType } from 'react'
import { Droplets, Heart, PartyPopper, ThumbsUp, type LucideProps } from 'lucide-react'
import { IconButton } from '@/components/play'
import { REACTION_KINDS, useStagePulse, type ReactionBurst } from '@/lib/watch/stagePulse'
import type { ReactionKind } from '@/lib/watch/types'
import { cn } from '@/lib/utils'
import './watch-together.css'

/** Each reaction's glyph and accessible name; its colour is the stylesheet's (`data-kind`). */
export const REACTIONS: Record<ReactionKind, { icon: ComponentType<LucideProps>; label: string }> = {
  kudos: { icon: ThumbsUp, label: 'Kudos' },
  cheers: { icon: PartyPopper, label: 'Cheers' },
  heart: { icon: Heart, label: 'Love' },
  sweat: { icon: Droplets, label: 'Sweat' },
}

/** The most reactions floating at once: a burst from a big audience stays a handful, not a cloud. */
export const MAX_FLOATING = 24
/** The most of one burst that float: a pulse with 40 hearts floats a few, staggered. */
const MAX_PER_BURST = 4
/** Between the floats of one burst. */
const BURST_STAGGER_MS = 140

interface Floater {
  id: number
  kind: ReactionKind
  own: boolean
  /** Sideways drift at the top, in px, and how long the climb takes. */
  drift: number
  duration: number
  /** Where it starts across the lane, 0–1. */
  lane: number
}

let floaterSequence = 0

function floater(kind: ReactionKind, own: boolean): Floater {
  return {
    id: ++floaterSequence,
    kind,
    own,
    drift: Math.round((Math.random() - 0.5) * 72),
    duration: Math.round(2600 + Math.random() * 1200),
    lane: Math.random(),
  }
}

/**
 * The stage's reactions floating up over the canvas (docs/features/watch-together.md): each one a Play disc with its
 * glyph, rising from the foot's right-hand lane with a little sway and fading near the top, on every screen watching
 * the stage. This viewer's own float at once and a little larger. At most `MAX_FLOATING` at a time; a burst floats at
 * most a few. Decorative for assistive technology: the bar's buttons say what was sent. Under reduced motion each one
 * fades in place instead of climbing.
 */
export const ReactionLayer = memo(function ReactionLayer({ className }: { className?: string }) {
  const store = useStagePulse()
  const [floaters, setFloaters] = useState<Floater[]>([])
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>())

  useEffect(() => {
    if (!store) return
    const pending = timers.current
    const add = (item: Floater) => setFloaters((current) => (current.length >= MAX_FLOATING ? current : [...current, item]))
    const off = store.onReaction(({ kind, count, own }: ReactionBurst) => {
      const shown = Math.min(count, MAX_PER_BURST)
      for (let index = 0; index < shown; index++) {
        if (index === 0) {
          add(floater(kind, own))
          continue
        }
        const timer = setTimeout(() => {
          pending.delete(timer)
          add(floater(kind, own))
        }, index * BURST_STAGGER_MS)
        pending.add(timer)
      }
    })
    return () => {
      off()
      pending.forEach(clearTimeout)
      pending.clear()
    }
  }, [store])

  const done = (id: number) => setFloaters((current) => current.filter((item) => item.id !== id))

  return (
    <div className={cn('watch-reactions', className)} aria-hidden="true">
      {floaters.map((item) => {
        const { icon: Icon } = REACTIONS[item.kind]
        return (
          <span
            key={item.id}
            className="watch-reactions__float"
            data-kind={item.kind}
            data-own={item.own || undefined}
            style={{
              ['--reaction-drift' as string]: `${item.drift}px`,
              ['--reaction-duration' as string]: `${item.duration}ms`,
              ['--reaction-lane' as string]: item.lane,
            }}
            onAnimationEnd={(event) => {
              if (event.target === event.currentTarget) done(item.id)
            }}
          >
            <Icon aria-hidden="true" />
          </span>
        )
      })}
    </div>
  )
})

/**
 * The four reactions as buttons (kudos, cheers, love, sweat), each its glyph on its colour with its name as label and
 * tooltip. A press floats it here at once and sends it to everyone watching the stage, signed in or not. Nothing
 * without the stage's pulse.
 */
export const ReactionBar = memo(function ReactionBar({ className }: { className?: string }) {
  const store = useStagePulse()
  if (!store) return null
  return (
    <div className={cn('watch-reaction-bar', className)} role="group" aria-label="Reactions">
      {REACTION_KINDS.map((kind) => {
        const { icon: Icon, label } = REACTIONS[kind]
        return (
          <IconButton key={kind} variant="ghost" size="compact" label={label} className="watch-reaction-bar__button" data-kind={kind} onClick={() => store.react(kind)}>
            <Icon />
          </IconButton>
        )
      })}
    </div>
  )
})
